/**
 * Map camera: world-space centre + zoom (css px per world unit) with critically damped motion.
 *
 *  - Wheel zoom is anchored: the world point under the cursor stays under the cursor for the
 *    whole animation (zoom is interpolated in log space).
 *  - `glideTo` eases slowly (bus 'focusHex'), `panTo` quickly (minimap clicks).
 *  - The view is clamped so it never leaves the paper sheet; fully zoomed out shows the sheet.
 */
import type { Rect } from './sheet';

interface Anchor {
  wx: number;
  wy: number;
  /** Screen offset of the anchor from the viewport centre (css px). */
  sx: number;
  sy: number;
}

export const MAX_ZOOM = 4;

export class Camera {
  x = 0;
  y = 0;
  zoom = 1;
  private tx = 0;
  private ty = 0;
  private tzoom = 1;
  private anchor: Anchor | null = null;
  private rate = 14;
  screenW = 1;
  screenH = 1;
  minZoom = 0.1;
  maxZoom = MAX_ZOOM;
  private bounds: Rect = { x0: 0, y0: 0, x1: 1e5, y1: 1e5 };
  /** Incremented whenever the rendered view changes. */
  version = 0;

  setBounds(r: Rect): void {
    this.bounds = r;
    this.fitLimits();
  }

  setScreen(w: number, h: number): void {
    this.screenW = Math.max(1, w);
    this.screenH = Math.max(1, h);
    this.fitLimits();
    this.version++;
  }

  private fitLimits(): void {
    const b = this.bounds;
    this.minZoom = Math.min(this.screenW / (b.x1 - b.x0), this.screenH / (b.y1 - b.y0));
    this.maxZoom = Math.max(MAX_ZOOM, this.minZoom);
    this.tzoom = this.clampZoom(this.tzoom);
    this.zoom = this.clampZoom(this.zoom);
  }

  private clampZoom(z: number): number {
    return Math.min(this.maxZoom, Math.max(this.minZoom, z));
  }

  /** Jump without animation. */
  set(x: number, y: number, zoom: number): void {
    this.zoom = this.tzoom = this.clampZoom(zoom);
    this.x = this.tx = x;
    this.y = this.ty = y;
    this.anchor = null;
    this.clampCentre();
    this.tx = this.x;
    this.ty = this.y;
    this.version++;
  }

  /** Zoom by `factor` keeping screen point (sx, sy) fixed. */
  zoomAt(factor: number, sx: number, sy: number): void {
    const [wx, wy] = this.screenToWorld(sx, sy);
    this.tzoom = this.clampZoom(this.tzoom * factor);
    this.anchor = { wx, wy, sx: sx - this.screenW / 2, sy: sy - this.screenH / 2 };
    this.rate = 14;
  }

  /** Drag-pan by a screen delta (immediate). */
  panBy(dx: number, dy: number): void {
    this.x -= dx / this.zoom;
    this.y -= dy / this.zoom;
    this.anchor = null;
    this.clampCentre();
    this.tx = this.x;
    this.ty = this.y;
    this.version++;
  }

  glideTo(x: number, y: number, zoom?: number, rate = 4): void {
    this.tx = x;
    this.ty = y;
    if (zoom !== undefined) this.tzoom = this.clampZoom(zoom);
    this.anchor = null;
    this.rate = rate;
  }

  /** Advance the animation; returns true if the view changed. */
  update(dt: number): boolean {
    const x0 = this.x;
    const y0 = this.y;
    const z0 = this.zoom;
    const k = 1 - Math.exp(-dt * this.rate);
    const kz = 1 - Math.exp(-dt * Math.min(this.rate, 12));
    const lz = Math.log(this.zoom);
    const ltz = Math.log(this.tzoom);
    this.zoom = Math.abs(ltz - lz) < 1e-4 ? this.tzoom : Math.exp(lz + (ltz - lz) * kz);
    if (this.anchor) {
      const a = this.anchor;
      this.x = a.wx - a.sx / this.zoom;
      this.y = a.wy - a.sy / this.zoom;
      this.tx = a.wx - a.sx / this.tzoom;
      this.ty = a.wy - a.sy / this.tzoom;
      if (this.zoom === this.tzoom) this.anchor = null;
    } else {
      this.x += (this.tx - this.x) * k;
      this.y += (this.ty - this.y) * k;
      if (Math.abs(this.tx - this.x) * this.zoom < 0.05 && Math.abs(this.ty - this.y) * this.zoom < 0.05) {
        this.x = this.tx;
        this.y = this.ty;
      }
    }
    this.clampCentre();
    const changed = this.x !== x0 || this.y !== y0 || this.zoom !== z0;
    if (changed) this.version++;
    return changed;
  }

  get moving(): boolean {
    return this.anchor !== null || this.zoom !== this.tzoom || this.x !== this.tx || this.y !== this.ty;
  }

  private clampCentre(): void {
    const b = this.bounds;
    const hw = this.screenW / (2 * this.zoom);
    const hh = this.screenH / (2 * this.zoom);
    this.x = b.x1 - b.x0 <= hw * 2 ? (b.x0 + b.x1) / 2 : Math.min(b.x1 - hw, Math.max(b.x0 + hw, this.x));
    this.y = b.y1 - b.y0 <= hh * 2 ? (b.y0 + b.y1) / 2 : Math.min(b.y1 - hh, Math.max(b.y0 + hh, this.y));
  }

  screenToWorld(sx: number, sy: number): [number, number] {
    return [this.x + (sx - this.screenW / 2) / this.zoom, this.y + (sy - this.screenH / 2) / this.zoom];
  }

  worldToScreen(wx: number, wy: number): [number, number] {
    return [(wx - this.x) * this.zoom + this.screenW / 2, (wy - this.y) * this.zoom + this.screenH / 2];
  }

  /** Visible world rectangle. */
  view(): Rect {
    const hw = this.screenW / (2 * this.zoom);
    const hh = this.screenH / (2 * this.zoom);
    return { x0: this.x - hw, y0: this.y - hh, x1: this.x + hw, y1: this.y + hh };
  }
}
